import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import BackArrow from '../../assets/images/Frame_18177.png';
import EmailOneIMG from '../../assets/images/email_one.png';
import WhatsappIconIMG from '../../assets/images/whatsapp-icon.png';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import InactivityTimer from '../../components/InactivityTimer';

const LabReports = () => {
    const [labReports, setLabReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [whatsappNo, setWhatsappNo] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tempWhatsappNo, setTempWhatsappNo] = useState('');
    const [selectedReport, setSelectedReport] = useState(null);
    const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
    const [viewingReportUrl, setViewingReportUrl] = useState('');
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [printIntent, setPrintIntent] = useState(false);
    const [printedReports, setPrintedReports] = useState(new Set());
    const [printingReport, setPrintingReport] = useState(null);
    const [patientFullName, setPatientFullName] = useState('');

    const navigate = useNavigate();
    const location = useLocation();
    const patientNameFromNav = location.state?.patientName || '';
    // const handleLogout = () => {
    //     localStorage.removeItem('kiosk_token');
    //     navigate("/", { replace: true });
    // };

    const token = useSelector((state) => state.auth.kiosk_token);

    // Validate token exists and redirect if not
    useEffect(() => {
        if (!token) {
            navigate('/login', { replace: true });
        }
    }, [token, navigate]);

    // Decode once for userId and mrno
    const decodedToken = token ? jwtDecode(token) : null;
    const userId = decodedToken?.id;
    const mrno = decodedToken?.mrno;

    useEffect(() => {
        const fetchLabReports = async () => {
            if (!token) return; // Skip if no token

            try {
                setLoading(true);
                const decodedToken = jwtDecode(token);
                const response = await axios.get(
                    `${process.env.REACT_APP_KIOSK_OPS_URL}/lab/reports/patientwise?mrno=${decodedToken.mrno}`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                        },
                    }
                );
                console.log("Lab Reports Data:", response.data);

                // Extract reports from response
                if (response.data && response.data.length > 0 && response.data[0].reports) {
                    setLabReports(response.data[0].reports);
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching lab reports:", err);
                setError(err.message);
                setLoading(false);
            }
        };

        const fetchPatientData = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/patients/details`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.data && response.data.patient) {
                    const patient = response.data.patient;
                    setWhatsappNo(patient.contactNumber);

                    // Format full name: prefix + firstName + middleName + lastName
                    const nameParts = [
                        patient.prefix,
                        patient.firstName,
                        patient.middleName,
                        patient.lastName
                    ].filter(part => part && part.trim() !== '');

                    const fullName = nameParts.join(' ');
                    setPatientFullName(fullName);
                }
            } catch (err) {
                console.error("Error fetching patient data from MySQL:", err);
                // Fallback to external API if MySQL fetch fails
                try {
                    const decodedToken = jwtDecode(token);
                    const response = await axios.get(`${process.env.REACT_APP_KIOSK_OPS_URL}/patient/registeredPatientDetails?input=${encodeURIComponent(decodedToken.mrno)}`, {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1')}`,
                        },
                    });

                    if (response.data && response.data.data && response.data.data.length > 0) {
                        const patientData = response.data.data[0];
                        setWhatsappNo(patientData.contactNo);

                        const nameParts = [
                            patientData.prefix,
                            patientData.firstName,
                            patientData.middleName,
                            patientData.lastName
                        ].filter(part => part && part.trim() !== '');

                        const fullName = nameParts.join(' ');
                        setPatientFullName(fullName);
                    }
                } catch (fallbackErr) {
                    console.error("Error fetching patient data from external API:", fallbackErr);
                }
            }
        };

        fetchLabReports();
        fetchPatientData();

        // Set patient name from navigation state
        if (patientNameFromNav) {
            setPatientFullName(patientNameFromNav);
        }
    }, [token, patientNameFromNav]); // Use token instead of decodedToken.mrno

    // Check print statuses after reports are loaded
    useEffect(() => {
        const checkPrintStatuses = async () => {
            if (!userId || labReports.length === 0) return;

            // Group reports by sampleId
            const groupedReports = labReports.reduce((acc, report) => {
                const key = report.sampleId;
                if (!acc[key]) {
                    acc[key] = [];
                }
                acc[key].push(report);
                return acc;
            }, {});

            const sampleIds = Object.keys(groupedReports);
            const printedSet = new Set();

            for (const sampleId of sampleIds) {
                try {
                    const response = await axios.get(
                        `${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/check-print-status`,
                        {
                            params: {
                                reportType: 'lab',
                                reportId: sampleId,
                                userId: userId
                            }
                        }
                    );
                    if (response.data.alreadyPrinted) {
                        printedSet.add(sampleId);
                    }
                } catch (err) {
                    console.error('Error checking print status:', err);
                }
            }
            setPrintedReports(printedSet);
        };

        checkPrintStatuses();
    }, [labReports, userId]);

    // Return early if no token to prevent rendering errors
    if (!token) {
        return null;
    }

    // Helper function to format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    // Group reports by sampleId for better organization
    const groupedReports = labReports.reduce((acc, report) => {
        const key = report.sampleId;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(report);
        return acc;
    }, {});

    // Create simplified report list (one entry per sample)
    const simplifiedReports = Object.entries(groupedReports).map(([sampleId, reports]) => {
        const firstReport = reports[0];
        return {
            sampleId: sampleId,
            testName: firstReport.profileName || firstReport.serviceName,
            testDate: formatDate(firstReport.orderedDate),
            status: firstReport.resultStatus,
            reportUrl: firstReport.reportUrl,
            allReports: reports
        };
    });

    // Function to track report view event to backend
    const trackViewReport = async (sampleId) => {
        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/view`, {
                reportType: 'lab',
                reportId: sampleId,
                userId,
            });
            // No need to handle response here as it's fire-and-forget for UI
        } catch (err) {
            console.error('Error tracking report view:', err);
        }
    };

    // Open modal to show PDF report and track view
    const handleShowReportModal = (reportUrl, sampleId) => {
        setViewingReportUrl(reportUrl);
        setViewModalOpen(true);
        trackViewReport(sampleId);
    };

    // Function to track print and open new tab for print
    const handlePrintReport = async (reportUrl, sampleId) => {
        try {
            await axios.post('/api/report-tracking/print', {
                reportType: 'lab',
                reportId: sampleId,
                userId,
            });
        } catch (err) {
            console.error('Error tracking report print:', err);
        }
        window.open(reportUrl, '_blank');
    };

    const handleViewReport = (reportUrl) => {
        if (reportUrl) {
            window.open(reportUrl, '_blank');
        }
    };

    const handleWhatsappClick = (report) => {
        setSelectedReport(report);
        setTempWhatsappNo(whatsappNo);
        setIsModalOpen(true);
    };
    const sendWhatsappReport = async (phoneNumber, report) => {
        console.log('Sending lab report:', report);
        try {
            setSendingWhatsapp(true);

            // Call backend proxy endpoint instead of WhatsApp API directly
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/whatsapp/send-lab-report`, {
                phoneNumber: phoneNumber,
                reportUrl: report.reportUrl,
                sampleId: report.sampleId,
                testName: report.testName,
                patientName: patientFullName || report.allReports[0]?.patientName || "Patient",
                mrn: report.allReports[0]?.mrn || "N/A"
            });

            console.log('WhatsApp sent successfully:', response.data);
            alert('Lab report sent successfully via WhatsApp!');
            setSendingWhatsapp(false);
            setIsModalOpen(false);
        } catch (err) {
            console.error('Error sending WhatsApp:', err);
            console.error('Error response:', err.response?.data);
            console.error('Error status:', err.response?.status);
            console.error('Error message:', err.message);

            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            alert(`Failed to send WhatsApp message: ${errorMessage}\n\nPlease check the console for details.`);
            setSendingWhatsapp(false);
        }
    };

    const handleModalSubmit = () => {
        if (tempWhatsappNo && tempWhatsappNo.length === 10) {
            setWhatsappNo(tempWhatsappNo);
            sendWhatsappReport(tempWhatsappNo, selectedReport);
        } else {
            alert('Please enter a valid 10-digit WhatsApp number');
        }
    };

    const handleChangeNumber = (report) => {
        setSelectedReport(report);
        setTempWhatsappNo(whatsappNo);
        setIsModalOpen(true);
    };

    return (
        <>
            <InactivityTimer />
            <div className='flex items-center'>
                <Link to="/dashboard" className="flex w-28 mt-6 ml-14 px-4 py-3 bg-[#0FA968] text-white font-semibold rounded-lg shadow hover:bg-[#138F5E] transition duration-200" style={{ fontFamily: 'Roboto', fontSize: '20px', fontWeight: '500', lineHeight: '23.44px' }}>
                    <img src={BackArrow} alt="Back" className="mr-2" style={{ height: '24px', width: '24px' }} />
                    Back
                </Link>
                <span className='mt-8 ml-4' style={{ fontFamily: 'Roboto', fontSize: '28px', fontWeight: '600', lineHeight: '32.81px' }}>
                    Lab Reports
                </span>
            </div>

            {/* Horizontal Line */}
            <hr className="mt-6 ml-14 mr-14 border-t-1 border-gray-300" />

            {loading ? (
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#0FA968] mx-auto mb-4"></div>
                        <p className="text-xl text-gray-600">Loading Lab Reports...</p>
                    </div>
                </div>
            ) : error ? (
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center">
                        <h1 className="text-4xl font-bold text-red-500 mb-4">Error</h1>
                        <p className="text-xl text-gray-600">{error}</p>
                    </div>
                </div>
            ) : (
                <>
                    {/* Patient Info Card */}
                    {labReports.length > 0 && (
                        <div className="mx-14 mt-6 bg-gradient-to-r from-[#0FA968] to-[#138F5E] text-white p-6 rounded-xl shadow-lg">
                            <div className="grid grid-cols-4 gap-4">
                                <div>
                                    <p className="text-sm opacity-90">Patient Name</p>
                                    <p className="text-lg font-semibold">{patientFullName || labReports[0].patientName || 'N/A'}</p>
                                </div>
                                <div>
                                    <p className="text-sm opacity-90">MRN</p>
                                    <p className="text-lg font-semibold">{labReports[0].mrn}</p>
                                </div>
                                <div>
                                    <p className="text-sm opacity-90">Age / Gender</p>
                                    <p className="text-lg font-semibold">{labReports[0].patientAge} / {labReports[0].gender}</p>
                                </div>
                                <div>
                                    <p className="text-sm opacity-90">Total Reports</p>
                                    <p className="text-lg font-semibold">{simplifiedReports.length}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Lab Reports Table */}
                    <div className="mx-14 my-6">
                        {simplifiedReports.length > 0 ? (
                            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
                                            <tr>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase">Test Name</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase">Test Date</th>
                                                <th className="px-6 py-4 text-left text-sm font-semibold uppercase">Status</th>
                                                <th className="px-6 py-4 text-center text-sm font-semibold uppercase">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {simplifiedReports.map((report, index) => (
                                                <tr key={report.sampleId} className={`hover:bg-blue-50 transition ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                                                    <td className="px-6 py-4">
                                                        <p className="font-semibold text-gray-800 text-base">{report.testName}</p>
                                                        <p className="text-xs text-gray-500">Sample ID: {report.sampleId}</p>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="text-gray-700 font-medium">{report.testDate}</span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                            {report.status}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4">
                                                        <div className="flex justify-center items-center space-x-2">
                                                            {/* <button
                                                                onClick={() => handleViewReport(report.reportUrl)}
                                                                className='bg-[#0FA968] text-white rounded-lg px-4 py-2 font-semibold hover:bg-[#138F5E] transition duration-200 shadow text-sm'>
                                                                View Full Report
                                                            </button>
                                                             */}
                                                            {/* <button
                                                                onClick={() => handleShowReportModal(report.reportUrl, report.sampleId)}
                                                                className="bg-[#0FA968] text-white rounded-lg px-4 py-2 font-semibold hover:bg-[#138F5E] transition duration-200 shadow text-sm">
                                                                View Full Report
                                                            </button> */}
                                                            <button
                                                                onClick={async () => {
                                                                    // Check if already printed
                                                                    if (printedReports.has(report.sampleId)) {
                                                                        alert('You can only print this report once from the kiosk. If you need another copy, please contact reception.');
                                                                        return;
                                                                    }

                                                                    try {
                                                                        setPrintingReport(report.sampleId);

                                                                        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/report-tracking/print`, {
                                                                            reportType: 'lab',
                                                                            reportId: report.sampleId,
                                                                            userId,
                                                                        });

                                                                        if (response.data.alreadyPrinted) {
                                                                            alert(response.data.message);
                                                                            setPrintedReports(prev => new Set([...prev, report.sampleId]));
                                                                            setPrintingReport(null);
                                                                            return;
                                                                        }

                                                                        // Mark as printed
                                                                        setPrintedReports(prev => new Set([...prev, report.sampleId]));

                                                                        // Fetch PDF as blob to avoid CORS issues
                                                                        const pdfResponse = await fetch(report.reportUrl);
                                                                        const blob = await pdfResponse.blob();
                                                                        const blobUrl = URL.createObjectURL(blob);

                                                                        // Create hidden iframe with blob URL
                                                                        const iframe = document.createElement('iframe');
                                                                        iframe.style.display = 'none';
                                                                        iframe.src = blobUrl;
                                                                        document.body.appendChild(iframe);

                                                                        iframe.onload = () => {
                                                                            try {
                                                                                iframe.contentWindow.print();
                                                                                // Cleanup
                                                                                setTimeout(() => {
                                                                                    document.body.removeChild(iframe);
                                                                                    URL.revokeObjectURL(blobUrl);
                                                                                    setPrintingReport(null);
                                                                                }, 1000);
                                                                            } catch (err) {
                                                                                console.error('Error printing:', err);
                                                                                document.body.removeChild(iframe);
                                                                                URL.revokeObjectURL(blobUrl);
                                                                                setPrintingReport(null);
                                                                            }
                                                                        };
                                                                    } catch (err) {
                                                                        console.error('Error tracking report print:', err);
                                                                        alert('Failed to print report. Please try again.');
                                                                        setPrintingReport(null);
                                                                    }
                                                                }}
                                                                disabled={printedReports.has(report.sampleId) || printingReport !== null}
                                                                className={`bg-transparent border border-[#0FA968] rounded-lg p-2 transition duration-200 flex items-center justify-center ${printedReports.has(report.sampleId) || printingReport !== null
                                                                    ? 'opacity-50 cursor-not-allowed bg-gray-200'
                                                                    : 'hover:bg-[#0FA968] hover:text-white'
                                                                    }`}
                                                                title={printedReports.has(report.sampleId) ? 'Already printed - Contact reception for more copies' : 'Print report'}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" viewBox="0 0 24 24" fill="green">
                                                                    <path d="M6 2h12v4H6V2zm12 6H6c-1.1 0-2 .9-2 2v6h4v4h8v-4h4v-6c0-1.1-.9-2-2-2zm-4 10H10v-2h4v2z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                className='bg-[#EFFEF7] rounded-lg px-3 py-2 border-2 border-[#0FA968] hover:bg-[#0FA968] hover:bg-opacity-10 transition duration-200'>
                                                                <img src={EmailOneIMG} alt='Email' className="w-5 h-5" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleWhatsappClick(report)}
                                                                disabled={sendingWhatsapp}
                                                                className='bg-[#EFFEF7] rounded-lg px-3 py-2 border-2 border-[#0FA968] hover:bg-[#0FA968] hover:bg-opacity-10 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed'>
                                                                <img src={WhatsappIconIMG} alt='Whatsapp' className="w-5 h-5" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center justify-center min-h-[40vh]">
                                <div className="text-center">
                                    <svg className="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <h3 className="text-2xl font-semibold text-gray-700 mb-2">No Lab Reports Found</h3>
                                    <p className="text-gray-500">There are no lab reports available for this patient.</p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Complete Session Button */}
                    {/* {labReports.length > 0 && (
                        <div className="flex justify-center mt-8 mb-8">
                            <button 
                                onClick={handleLogout} 
                                className='px-8 py-3 text-white bg-[#0FA968] rounded-lg font-semibold text-lg hover:bg-[#138F5E] transition duration-200 shadow-lg'>
                                Complete Session
                            </button>
                        </div>
                    )} */}
                </>
            )}

            {/* Modal for viewing report */}
            {viewModalOpen && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75 report-modal-container">
                    <div className="relative bg-white rounded-lg shadow-lg w-[90vw] h-[90vh]">
                        <button
                            onClick={() => {
                                setViewModalOpen(false);
                                setPrintIntent(false);
                            }}
                            className="absolute top-3 right-3 text-gray-600 hover:text-gray-900"
                            aria-label="Close"
                        >
                            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        <iframe
                            src={viewingReportUrl}
                            title="Lab Report"
                            className="w-full h-full rounded-lg report-modal-iframe"
                            frameBorder="0"
                        />
                    </div>
                </div>
            )}

            {/* WhatsApp Number Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-50">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-2xl font-bold text-gray-800">
                                {whatsappNo ? 'Change WhatsApp Number' : 'Enter WhatsApp Number'}
                            </h2>
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setTempWhatsappNo('');
                                }}
                                className="text-gray-400 hover:text-gray-600 transition">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        {whatsappNo && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <p className="text-sm text-blue-800">
                                    Current Number: <span className="font-semibold">{whatsappNo}</span>
                                </p>
                            </div>
                        )}

                        <div className="mb-6">
                            <label className="block text-gray-700 font-semibold mb-2">
                                WhatsApp Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="tel"
                                maxLength="10"
                                placeholder="Enter 10-digit number"
                                value={tempWhatsappNo}
                                onChange={(e) => {
                                    const value = e.target.value.replace(/\D/g, '');
                                    setTempWhatsappNo(value);
                                }}
                                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-[#0FA968] focus:outline-none text-lg"
                            />
                            {tempWhatsappNo && tempWhatsappNo.length !== 10 && (
                                <p className="text-red-500 text-sm mt-1">Please enter a valid 10-digit number</p>
                            )}
                        </div>

                        <div className="flex gap-3">
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setTempWhatsappNo('');
                                }}
                                className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 transition duration-200">
                                Cancel
                            </button>
                            <button
                                onClick={handleModalSubmit}
                                disabled={sendingWhatsapp || !tempWhatsappNo || tempWhatsappNo.length !== 10}
                                className="flex-1 px-6 py-3 bg-[#0FA968] text-white rounded-lg font-semibold hover:bg-[#138F5E] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                                {sendingWhatsapp ? (
                                    <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <img src={WhatsappIconIMG} alt='WhatsApp' className="w-5 h-5" />
                                        Send Report
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* Full-screen loader overlay during print */}
            {printingReport !== null && (
                <div className="fixed inset-0 z-50 flex justify-center items-center bg-black bg-opacity-75">
                    <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#0FA968] border-t-transparent mx-auto mb-4"></div>
                        <p className="text-xl font-semibold text-gray-800">Preparing to print...</p>
                        <p className="text-sm text-gray-600 mt-2">Please wait while we process your report</p>
                    </div>
                </div>
            )}
        </>
    );
};

export default LabReports;